import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, Spinner, Text } from "@fluentui/react-components";
import { CloudArrowDownRegular, Dismiss24Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import * as ComponentFunctions from "./FileViewer.Functions";
import { FileUploadServices } from "../../services/FileUpload.Services";
import { ErrorType } from "../../models/HttpError";
import "./FileViewer.Styles.css";

const inferExtensionFromToken = (token: string): string => {
  const matchers: Array<[RegExp, string]> = [
    [/wordprocessingml/, "docx"],
    [/msword/, "doc"],
    [/spreadsheetml/, "xlsx"],
    [/excel/, "xls"],
    [/csv/, "csv"],
    [/pdf/, "pdf"],
    [/jpeg|jpg/, "jpg"],
    [/png/, "png"],
    [/gif/, "gif"],
    [/webp/, "webp"],
  ];

  const matched = matchers.find(([pattern]) => pattern.test(token));
  return matched ? matched[1] : "";
};

const inferExtension = (fileExtension?: string, contentType?: string, fileName?: string): string => {
  const normalizedExt = (fileExtension || "").trim().toLowerCase().replace(/^\./, "");
  const fromExt = normalizedExt ? (inferExtensionFromToken(normalizedExt) || normalizedExt) : "";
  if (fromExt) return fromExt;

  const normalizedContentType = (contentType || "").trim().toLowerCase();
  const fromContentType = normalizedContentType ? inferExtensionFromToken(normalizedContentType) : "";
  if (fromContentType) return fromContentType;

  const fromName = (fileName || "").trim().toLowerCase();
  const nameParts = fromName.split(".");
  return nameParts.length > 1 ? (nameParts.at(-1) || "") : "";
};

const getDisplayType = (ext?: string, contentType?: string, fileName?: string) => {
  const lowered = inferExtension(ext, contentType, fileName);
  if (!lowered) return "other";
  if (["pdf"].includes(lowered)) return "pdf";
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(lowered)) return "image";
  if (["doc", "docx"].includes(lowered)) return "word";
  if (["xls", "xlsx", "csv"].includes(lowered)) return "excel";
  return "other";
};

const buildWordPreviewHtml = async (
  fileExtension: string | undefined,
  contentType: string | undefined,
  fileName: string | undefined,
  fileBlob: Blob
): Promise<string | undefined> => {
  const ext = inferExtension(fileExtension, contentType, fileName);
  if (ext !== "docx") return undefined;

  const mammoth = await import("mammoth");
  const arrayBuffer = await fileBlob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value;
};

const buildExcelPreviewData = async (
  fileBlob: Blob
): Promise<{ sheetNames: string[]; sheetHtmlByName: Record<string, string> }> => {
  const XLSX = await import("xlsx");
  const arrayBuffer = await fileBlob.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const sheetNames = workbook.SheetNames || [];
  const sheetHtmlByName = sheetNames.reduce((acc: Record<string, string>, sheetName: string) => {
    const sheet = workbook.Sheets[sheetName];
    acc[sheetName] = sheet ? XLSX.utils.sheet_to_html(sheet) : "";
    return acc;
  }, {});

  return { sheetNames, sheetHtmlByName };
};

const FileViewerComponent = (props: ComponentFunctions.Props) => {
  const { isOpen, file, onClose, accessToken } = props;
  const [fileBlob, setFileBlob] = useState<Blob | undefined>();
  const [blobUrl, setBlobUrl] = useState<string | undefined>();
  const [previewHtml, setPreviewHtml] = useState<string | undefined>();
  const [excelSheetNames, setExcelSheetNames] = useState<string[]>([]);
  const [excelSheetHtmlByName, setExcelSheetHtmlByName] = useState<Record<string, string>>({});
  const [selectedExcelSheet, setSelectedExcelSheet] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorType | undefined>();

  const displayType = useMemo(
    () => getDisplayType(file?.fileExtension, file?.contentType, file?.fileName),
    [file?.fileExtension, file?.contentType, file?.fileName]
  );

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file?.fileName || "archivo";
    a.click();
  };

  useEffect(() => {
    let revoked = false;
    const fetchFile = async () => {
      if (!isOpen || !file?.id || !accessToken) return;
      setIsLoading(true);
      setError(undefined);
      setPreviewHtml(undefined);
      setExcelSheetNames([]);
      setExcelSheetHtmlByName({});
      setSelectedExcelSheet(undefined);
      try {
        const download = await FileUploadServices.downloadFile(file.id, accessToken);
        if (revoked) return;

        const normalizedBlob =
          displayType === "pdf" && download.file.type !== "application/pdf"
            ? new Blob([download.file], { type: "application/pdf" })
            : download.file;

        setFileBlob(normalizedBlob);
        const url = URL.createObjectURL(normalizedBlob);
        setBlobUrl(url);
      } catch (err: any) {
        setError({
          ErrorCode: 500,
          ErrorMessage: err?.message || "No se pudo cargar el archivo",
          Errors: [],
        });
      } finally {
        if (!revoked) setIsLoading(false);
      }
    };
    fetchFile();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(undefined);
      setFileBlob(undefined);
      setPreviewHtml(undefined);
      setExcelSheetNames([]);
      setExcelSheetHtmlByName({});
      setSelectedExcelSheet(undefined);
    };
  }, [isOpen, file?.id, accessToken, displayType]);

  useEffect(() => {
    let cancelled = false;

    const buildPreview = async () => {
      if (!fileBlob) return;

      try {
        if (displayType === "word") {
          const html = await buildWordPreviewHtml(file?.fileExtension, file?.contentType, file?.fileName, fileBlob);
          if (!cancelled) setPreviewHtml(html);
          return;
        }

        if (displayType === "excel") {
          const excelPreview = await buildExcelPreviewData(fileBlob);
          const firstSheet = excelPreview.sheetNames[0];
          if (!cancelled) {
            setExcelSheetNames(excelPreview.sheetNames);
            setExcelSheetHtmlByName(excelPreview.sheetHtmlByName);
            setSelectedExcelSheet(firstSheet);
            setPreviewHtml(firstSheet ? excelPreview.sheetHtmlByName[firstSheet] : undefined);
          }
          return;
        }

        if (!cancelled) setPreviewHtml(undefined);
      } catch (err: any) {
        if (!cancelled) {
          setPreviewHtml(undefined);
          setError({
            ErrorCode: 500,
            ErrorMessage: err?.message || "No se pudo generar la vista previa del archivo",
            Errors: [],
          });
        }
      }
    };

    buildPreview();

    return () => {
      cancelled = true;
    };
  }, [fileBlob, displayType, file?.fileExtension, file?.contentType, file?.fileName]);

  useEffect(() => {
    if (displayType !== "excel") return;
    if (!selectedExcelSheet) return;
    setPreviewHtml(excelSheetHtmlByName[selectedExcelSheet]);
  }, [displayType, selectedExcelSheet, excelSheetHtmlByName]);

  const renderContent = () => {
    if (isLoading) return <div className="pdf-viewer-content"><Spinner label="Cargando archivo..." /></div>;
    if (error) return <div className="pdf-viewer-content"><Text>{error.ErrorMessage}</Text></div>;
    if (!blobUrl) return <div className="pdf-viewer-content"><Text>Sin archivo para mostrar</Text></div>;

    switch (displayType) {
      case "image":
        return (
          <div className="pdf-viewer-content">
            <img src={blobUrl} alt={file?.fileName} className="pdf-viewer-image" />
          </div>
        );
      case "pdf":
        return (
          <div className="pdf-viewer-content">
            <iframe src={blobUrl} title={file?.fileName} className="pdf-viewer-frame" />
          </div>
        );
      case "word":
      case "excel":
        if (previewHtml) {
          return (
            <div className="pdf-viewer-content">
              <div className="pdf-viewer-html-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          );
        }
        return (
          <div className="pdf-viewer-content">
            <Text>No fue posible generar vista previa para este archivo. Podés descargarlo.</Text>
          </div>
        );
      default:
        return (
          <div className="pdf-viewer-content">
            <Text>Formato no soportado para vista previa. Podés descargarlo.</Text>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className="pdf-viewer-surface" aria-describedby={undefined}>
        <DialogBody className="pdf-viewer-body">
          <DialogTitle
            className="pdf-viewer-title"
            action={
              <div className="pdf-viewer-title-actions">
                {blobUrl && (
                  <Button
                    appearance="subtle"
                    icon={<CloudArrowDownRegular />}
                    onClick={handleDownload}
                    aria-label="Descargar"
                    title="Descargar"
                  />
                )}
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={onClose}
                  aria-label="Cerrar"
                  title="Cerrar"
                />
              </div>
            }
          >
            <span className="pdf-viewer-title-text">{file?.fileName || "Documento"}</span>
          </DialogTitle>
          <DialogContent className="pdf-viewer-dialog-content">
            {displayType === "word" && inferExtension(file?.fileExtension, file?.contentType, file?.fileName) === "docx" && previewHtml && (
              <div className="pdf-viewer-file-badge">Vista previa DOCX</div>
            )}
            {displayType === "excel" && excelSheetNames.length > 0 && (
              <div className="pdf-viewer-excel-toolbar">
                <label htmlFor="excel-sheet-selector">Hoja:</label>
                <select
                  id="excel-sheet-selector"
                  className="pdf-viewer-excel-select"
                  value={selectedExcelSheet || ""}
                  onChange={(event) => setSelectedExcelSheet(event.target.value)}
                >
                  {excelSheetNames.map((sheetName: string) => (
                    <option key={sheetName} value={sheetName}>
                      {sheetName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {renderContent()}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const ConnectedFileViewer = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(FileViewerComponent as any);

export default ConnectedFileViewer as React.ComponentType<ComponentFunctions.Props>;
